/**
 * Cierre semestral — informe financiero congelado del semestre (Fase 5 del
 * plan de morosidad, 2026-10-08). Se genera solo terminado el periodo y en
 * orden; una vez generado no cambia. Sin correos a los vecinos. Los saldos son
 * los calculados al cierre del día; el registro del banco va como control.
 */
import { apiFetch } from './client';
import type { ItemComposicion } from './reportes-tesoreria';

export interface EstadoCierres {
  /** Periodo que corresponde cerrar (`AAAA-S1|S2`); null = no hay cuotas. */
  periodo: string | null;
  etiqueta: string | null;
  disponible_desde: string | null;
  disponible: boolean;
}

export interface CierreSemestral {
  id: string;
  periodo: string;
  etiqueta: string;
  fecha_corte: string;
  cerrado_at: string;
  cerrado_por_usuario_id: string;
  ingresos: number;
  gastos: number;
  resultado: number;
  saldo_inicial: number | null;
  saldo_inicial_fecha: string | null;
  saldo_final: number | null;
  saldo_final_fecha: string | null;
  /** Control: último saldo registrado en Banco hasta el corte, con su fecha real. */
  saldo_final_banco: number | null;
  saldo_final_banco_fecha: string | null;
  /** Casas con deuda al corte y su deuda total. */
  total_lotes_morosos: number;
  total_monto_moroso: number;
  acta_url: string | null;
}

export interface CuotaAlCorte {
  cuota_id: string;
  periodo: string;
  etiqueta: string;
  monto_total: number;
  monto_aplicado: number;
  monto_moroso: number;
}

export interface CasaConDeuda {
  lote_id: string;
  lote_identificador: string;
  cuotas_afectadas: CuotaAlCorte[];
  monto_moroso_total: number;
}

export interface AjustePeriodoCerrado {
  periodo: string;
  etiqueta: string;
  ingresos: number;
  gastos: number;
}

export interface ResumenAnualCierre {
  anio: number;
  ingresos: number;
  gastos: number;
  ingresos_por_tipo: ItemComposicion[];
  gastos_por_categoria: ItemComposicion[];
  saldo_inicial: number | null;
  saldo_inicial_fecha: string | null;
  saldo_final: number | null;
  saldo_final_fecha: string | null;
  saldo_final_banco: number | null;
  saldo_final_banco_fecha: string | null;
}

export interface CierreSemestralDetalle extends CierreSemestral {
  ingresos_por_tipo: ItemComposicion[];
  gastos_por_categoria: ItemComposicion[];
  ajustes: AjustePeriodoCerrado[];
  /** Solo en el 2° semestre. */
  resumen_anual: ResumenAnualCierre | null;
  casas: CasaConDeuda[];
}

/** Lo que muestra el reporte al abrirse: qué toca cerrar y los ya cerrados. */
export interface CierresSemestralesResponse {
  estado: EstadoCierres;
  cierres: CierreSemestral[];
}

export async function getCierresSemestrales(): Promise<CierresSemestralesResponse> {
  const [estado, cierres] = await Promise.all([
    apiFetch<EstadoCierres>('/cierres-semestrales/estado'),
    apiFetch<CierreSemestral[]>('/cierres-semestrales/'),
  ]);
  return { estado, cierres };
}

export function getCierreSemestral(id: string): Promise<CierreSemestralDetalle> {
  return apiFetch<CierreSemestralDetalle>(`/cierres-semestrales/${id}`);
}

/** Genera y congela el cierre. Irreversible. */
export function generarCierreSemestral(
  periodo: string,
): Promise<CierreSemestralDetalle> {
  return apiFetch<CierreSemestralDetalle>('/cierres-semestrales/', {
    method: 'POST',
    body: JSON.stringify({ periodo }),
  });
}
